import { ZF_NO } from '@mp/constant'
import crypto from 'crypto-js'
import dayjs from 'dayjs'
import { getDeviceCode } from '..'

/**
 * 全局类型声明，为Window对象添加deviceId属性
 */
declare global {
  interface Window {
    deviceId?: string
    SignInitContrRes?: boolean
  }
}

export interface SecurityPublicKeyResponse {
  publicKeyRaw: string
  publicKeyX509: string
  keySerialNo: string
  keyType: string
  expiredAt: string
  /** 距过期剩余秒数（服务端时间），写入缓存时用于换算本机 expiredAt */
  expiresInSeconds?: number
}

/** 公钥拉取失败后最多重试次数（不含首次请求） */
const PUBLIC_KEY_MAX_RETRIES = 2

export async function SecurityPublicKey(): Promise<SecurityPublicKeyResponse> {
  let lastError: unknown

  for (let attempt = 0; attempt <= PUBLIC_KEY_MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(`${import.meta.env.VITE_SDPMCHAUTH}/v4/security/publicKey`, {
        headers: {
          appId: ZF_NO,
          deviceId: await getDeviceCode(),
        },
      })

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`)

      const data = await response.json()
      const result = data?.data
      if (!result)
        throw new Error('公钥不存在')

      return result
    }
    catch (error) {
      lastError = error
      console.error(`获取公钥失败 (第 ${attempt + 1}/${PUBLIC_KEY_MAX_RETRIES + 1} 次):`, error)
      if (attempt < PUBLIC_KEY_MAX_RETRIES)
        await new Promise(resolve => setTimeout(resolve, 300 * (attempt + 1)))
    }
  }

  throw lastError
}

/**
 * 生成指定长度的随机字符串
 * @param length 随机字符串的长度
 * @returns 生成的随机字符串
 */
export function generateRandomString(length: number): string {
  const RANDOM_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
  let randomString = ''

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * RANDOM_CHARS.length)
    randomString += RANDOM_CHARS.charAt(randomIndex)
  }

  return randomString
}

/**
 * 将十六进制字符串转换为16位字符串
 * @param hexString 十六进制字符串
 * @returns 16位字符串
 */
export function hexToString16(hexString: string): string {
  try {
    // 将十六进制字符串转换为WordArray
    const wordArray = crypto.enc.Hex.parse(hexString)
    // 转换为Base64并移除填充字符
    const base64 = crypto.enc.Base64.stringify(wordArray).replace(/=/g, '')
    // 截取前16位
    return base64.substring(0, 16)
  }
  catch (error) {
    console.error('十六进制转16位字符串失败', error)
    throw new Error('十六进制转16位字符串失败')
  }
}

/**
 * 将Uint8Array转换为Base64字符串
 * @param buffer 输入的Uint8Array缓冲区
 * @returns 转换后的Base64字符串
 */
export function arrayBufferToBase64(buffer: Uint8Array): string {
  // 将Uint8Array转换为二进制字符串
  const binaryString = String.fromCharCode.apply(null, Array.from(buffer))
  // 使用btoa将二进制字符串转换为Base64
  return btoa(binaryString)
}

/**
 * 将Base64字符串解码为Uint8Array
 * @param base64 输入的Base64字符串
 * @returns 解码后的Uint8Array
 */
export function base64ToArrayBuffer(base64: string): Uint8Array {
  // 使用atob将Base64解码为二进制字符串
  const binaryString = atob(base64)
  // 创建一个Uint8Array来存储结果
  const bytes = new Uint8Array(binaryString.length)
  // 将每个字符的字符码复制到Uint8Array中
  for (let i = 0; i < binaryString.length; i++)
    bytes[i] = binaryString.charCodeAt(i)

  return bytes
}

/**
 * 将Uint8Array转换为16进制字符串
 * @param bytes Uint8Array字节数组
 * @returns 16进制字符串
 */
export function bytesToHex(bytes: Uint8Array): string {
  // 使用 crypto-js 将 Uint8Array 转换为 WordArray
  const wordArray = crypto.lib.WordArray.create(bytes as any)
  // 使用 crypto-js 的 Hex 编码器将 WordArray 转换为十六进制字符串
  return crypto.enc.Hex.stringify(wordArray)
}

/**
 * 十六进制字符串转Base64
 * @param hexString 十六进制字符串
 * @returns Base64编码的字符串
 */
export function hexToBase64(hexString: string): string {
  // 将十六进制字符串转换为 WordArray
  const wordArray = crypto.enc.Hex.parse(hexString)
  // 将 WordArray 转换为 Base64
  return crypto.enc.Base64.stringify(wordArray)
}

/** 进行中的公钥拉取 Promise，用于并发去重 */
let pendingPublicKeyFetch: Promise<SecurityPublicKeyResponse> | null = null

function readCachedPublicKey(): SecurityPublicKeyResponse | null {
  const raw = sessionStorage.getItem('publicKeyRes')
  if (!raw)
    return null

  try {
    return JSON.parse(raw) as SecurityPublicKeyResponse
  }
  catch {
    sessionStorage.removeItem('publicKeyRes')
    return null
  }
}

function isPublicKeyExpired(publicKeyRes: SecurityPublicKeyResponse): boolean {
  return dayjs().isAfter(dayjs(publicKeyRes.expiredAt))
}

/**
 * 将服务端公钥转为可缓存结构：用 expiresInSeconds 在本机推算过期时刻，
 * 避免用户系统时间不准导致与服务端 expiredAt 比较出错
 */
function buildCachedPublicKey(res: SecurityPublicKeyResponse): SecurityPublicKeyResponse {
  const { expiresInSeconds } = res
  if (expiresInSeconds == null || expiresInSeconds <= 0)
    return res

  return {
    ...res,
    expiredAt: dayjs().add(expiresInSeconds, 'second').format('YYYY-MM-DD HH:mm:ss'),
  }
}

async function fetchAndCachePublicKey(): Promise<SecurityPublicKeyResponse> {
  const cached = readCachedPublicKey()
  if (cached && !isPublicKeyExpired(cached))
    return cached

  if (cached)
    sessionStorage.removeItem('publicKeyRes')

  const res = await SecurityPublicKey()
  if (!res)
    throw new Error('公钥不存在')

  const cachedPublicKey = buildCachedPublicKey(res)
  sessionStorage.setItem('publicKeyRes', JSON.stringify(cachedPublicKey))
  return cachedPublicKey
}

/**
 * 获取公钥信息，支持缓存和过期检查
 * 优先从sessionStorage读取缓存的公钥，如果不存在则重新获取
 * 如果公钥已过期，会清除缓存并重新拉取
 * 并发请求共享同一次 in-flight 拉取，避免轮换日重复请求
 * @returns Promise<SecurityPublicKeyResponse> 公钥响应对象
 */
export async function getPublicKey(): Promise<SecurityPublicKeyResponse> {
  const cached = readCachedPublicKey()
  if (cached && !isPublicKeyExpired(cached))
    return cached

  if (!pendingPublicKeyFetch) {
    pendingPublicKeyFetch = fetchAndCachePublicKey().finally(() => {
      pendingPublicKeyFetch = null
    })
  }

  return pendingPublicKeyFetch
}

/**
 * 将对象转换为URL查询字符串格式
 * @param obj 要转换的对象
 * @returns 查询字符串格式的字符串，例如 "a=1&b=2&c=3"
 */
export function objectToQueryString(obj: Record<string, any>): string {
  if (!obj || typeof obj !== 'object')
    return ''

  return Object.entries(obj)
    .filter(([_, value]) => value !== null && value !== undefined)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&')
}

/**
 * 将对象转换为原始查询字符串格式（不进行URL编码）
 * 用于签名计算等需要原始字符串的场景
 * @param obj 要转换的对象
 * @returns 查询字符串格式的字符串，例如 "a=1&b=2&c=3"
 */
export function objectToRawQueryString(obj: Record<string, any>): string {
  if (!obj || typeof obj !== 'object')
    return ''

  return Object.entries(obj)
    .filter(([_, value]) => value !== null && value !== undefined)
    .map(([key, value]) => `${key}=${value}`)
    .join('&')
}

// wujie 微前端环境兼容处理
export function getGlobalWindow(): Window {
  try {
    // 优先使用当前 window，如果无法访问则使用 parent
    return window.self === window.top ? window : (window.parent || window)
  }
  catch (e) {
    // 如果出现跨域问题，回退到当前 window
    return window
  }
}
